import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import { Transaction } from '../../../types/transactions';
import { Machine } from '../../../types/machines';

export function useTransactionsList() {
  const [items, setItems] = useState<Transaction[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState(''); // search productId
  const [machineFilter, setMachineFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [txRes, mRes] = await Promise.all([
        api.get<{ data: Transaction[] }>("/transactions"),
        api.get<{ data: Machine[] }>("/machines"),
      ]);
      setItems((txRes as any).data || (txRes as any));
      setMachines((mRes as any).data || (mRes as any));
    } catch (e: any) {
      setError(e.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (machineFilter) arr = arr.filter(t => String(t.machineId) === String(machineFilter));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter(t => (t.productId || '').toLowerCase().includes(q) || String(t.orderId || '').toLowerCase().includes(q));
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      arr = arr.filter(t => t.createdAt ? new Date(t.createdAt) >= from : true);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      // include entire day end
      to.setHours(23,59,59,999);
      arr = arr.filter(t => t.createdAt ? new Date(t.createdAt) <= to : true);
    }
    return arr;
  }, [items, machineFilter, query, dateFrom, dateTo]);

  const total = filtered.length;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageData = filtered.slice(start, end);

  return {
    items: pageData,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    query,
    setQuery,
    machines,
    machineFilter,
    setMachineFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    loading,
    error,
    refresh: fetchAll,
  };
}
